import { customElement, property } from 'lit/decorators.js';
import { CSSResult, html, LitElement, nothing, unsafeCSS } from 'lit';
import { Events, Messages, Product, ProductIdentifierContainer } from '../../../@types/wishlist.js';
import '../event-layer-content/event-layer-content.js';
import styles from './event-app-layer.scss?inline';
import { provide } from '@lit/context';
import { productsContext } from '../../../scripts/context/wishlistContext.js';
import { ProductsState, ProductsStateImpl } from '../../../scripts/context/wishlist-state.js';
import { i18nContext } from '../../../scripts/context/i18nContext.js';
import { WishlistAppConfig, wishlistAppConfigContext } from '../../../scripts/context/appConfigContext.js';
import { closeNativeDialog, executeOnOriginPage, openDeeplink } from '@mobile-app/native-dialog';
import { sharedCss } from '../../../scripts/style-bundle/shared.js';
import { loadFromDOM } from '../../../scripts/utils/load-from-dom.js';

@customElement('ents-event-app-layer')
export class EventAppLayer extends LitElement {
  @property({ type: Boolean, attribute: 'logged-in' })
  private loggedIn: boolean = false;

  @provide({ context: productsContext })
  @property({ attribute: false })
  private productsState: ProductsState = this.createProductState(this, []);

  static get styles(): CSSResult[] {
    return [sharedCss, unsafeCSS(styles)];
  }

  private createProductState(outerThis: this, products: Product[]): ProductsState {
    return new (class extends ProductsStateImpl {
      entities = products;
      updateProductsAndSharedProducts = (newProducts: Product[]): void => {
        outerThis.productsState = outerThis.createProductState(outerThis, newProducts);
      };
    })();
  }

  @provide({ context: i18nContext })
  private messages!: Messages;

  @provide({ context: wishlistAppConfigContext })
  private wishlistAppConfig!: WishlistAppConfig;

  private readonly events: Events;

  constructor() {
    super();
    const sharedCollection = loadFromDOM('#ents-wishlist-shared-collection');
    this.events = loadFromDOM('#ents-wishlist-events');
    this.messages = loadFromDOM('#ents-wishlist-messages');
    this.productsState.updateProductsAndSharedProducts(
      loadFromDOM('#ents-wishlist-products'),
      sharedCollection?.products,
    );
    this.wishlistAppConfig = loadFromDOM('#ents-wishlist-app-config');
  }

  connectedCallback() {
    super.connectedCallback();

    executeOnOriginPage(`
      document.dispatchEvent(new CustomEvent('ents:wishlist-native-dialog-opened', {bubbles: true, composed: true}));
    `);
  }

  private readonly handleScrollToEvent = (e: CustomEvent<ProductIdentifierContainer>) => {
    executeOnOriginPage(`
      document.dispatchEvent(new CustomEvent('ents:wishlist-scroll-to-product', {
        detail: { identifier: '${e.detail.identifier}' },
        bubbles: true,
        composed: true
      }));
    `);
    closeNativeDialog();
  };

  private readonly activateNotificationsDeeplink = () => {
    openDeeplink('breuningerapp://pushSettings');
  };

  private renderNotificationHint() {
    return html` <wishlist-alert
      local-storage-id="ents-wishlist-event-notification-hint"
      class="ents-event-app-layer__notification-hint"
    >
      <p class="ents-copy-small-bold">
        ${this.messages['ents_wishlist_event_layer_app_notification_hint']}<br />
        <a href="#" @click="${this.activateNotificationsDeeplink}" class="bdk-link-primary" size="small">
          ${this.messages['ents_wishlist_event_layer_app_notification_linktext']}
        </a>
      </p>
    </wishlist-alert>`;
  }

  render() {
    return html` <div class="ents-event-app-layer" data-test-ents-event-layer>
      <ents-event-layer-content
        ?logged-in="${this.loggedIn}"
        shown
        .events="${this.events}"
        @ents-wishlist-event-item-clicked="${this.handleScrollToEvent}"
      ></ents-event-layer-content>
      ${this.loggedIn ? this.renderNotificationHint() : nothing}
    </div>`;
  }
}
